import React, { useEffect, useRef } from 'react';
import anime from 'animejs';

interface LaunchScreenProps {
  onComplete: () => void;
}

export const LaunchScreen: React.FC<LaunchScreenProps> = ({ onComplete }) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const logoRef = useRef<HTMLHeadingElement | null>(null);
  const taglineRef = useRef<HTMLParagraphElement | null>(null);
  const sweepRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    // Play short synthetic Web Audio startup sound signature (gentle chord chime)
    try {
      const AudioContextClass =
        window.AudioContext ||
        (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      if (AudioContextClass) {
        const ctx = new AudioContextClass();
        // If browser autoplay blocks, ctx.state might be suspended; ignore gracefully
        if (ctx.state === 'running') {
          const now = ctx.currentTime;
          const osc1 = ctx.createOscillator();
          const osc2 = ctx.createOscillator();
          const gain = ctx.createGain();

          osc1.type = 'sine';
          osc1.frequency.setValueAtTime(220, now + 0.3); // A3
          osc1.frequency.exponentialRampToValueAtTime(440, now + 0.7); // A4

          osc2.type = 'triangle';
          osc2.frequency.setValueAtTime(330, now + 0.3); // E4
          osc2.frequency.exponentialRampToValueAtTime(660, now + 0.7); // E5

          gain.gain.setValueAtTime(0.0001, now);
          gain.gain.setTargetAtTime(0.20, now + 0.3, 0.08);
          gain.gain.setTargetAtTime(0.0001, now + 0.75, 0.15);

          osc1.connect(gain);
          osc2.connect(gain);
          gain.connect(ctx.destination);

          osc1.start(now + 0.3);
          osc2.start(now + 0.3);
          osc1.stop(now + 1.1);
          osc2.stop(now + 1.1);
        }
      }
    } catch {
      // Safe fallback if audio blocked
    }

    // Anime.js 1-second automotive startup animation timeline
    const tl = anime.timeline({
      easing: 'easeOutQuad',
      complete: () => {
        onComplete();
      },
    });

    tl.add({
      targets: sweepRef.current,
      translateX: ['-100%', '100%'],
      opacity: [0, 0.8, 0],
      duration: 700,
      offset: 150,
    })
      .add(
        {
          targets: logoRef.current,
          opacity: [0, 1],
          scale: [0.92, 1.05],
          duration: 600,
          easing: 'easeOutCubic',
        },
        300
      )
      .add(
        {
          targets: taglineRef.current,
          opacity: [0, 1],
          translateY: [8, 0],
          duration: 400,
        },
        500
      )
      .add(
        {
          targets: containerRef.current,
          opacity: [1, 0],
          duration: 250,
          easing: 'easeInQuad',
        },
        950
      );
  }, [onComplete]);

  return (
    <div
      ref={containerRef}
      className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-black select-none overflow-hidden"
    >
      {/* Subtle Light Sweep */}
      <div
        ref={sweepRef}
        className="absolute w-72 h-72 rounded-full bg-cyan-500/20 blur-3xl pointer-events-none"
      />

      <div className="relative z-10 flex flex-col items-center text-center">
        <div className="flex items-center gap-2 mb-2">
          <span className="w-2.5 h-2.5 rounded-full bg-red-600 shadow-[0_0_12px_rgba(220,38,38,0.9)] animate-pulse" />
          <span className="text-[10px] font-mono tracking-[0.4em] text-slate-400 uppercase font-bold">
            AUTOMOTIVE SOUND PLATFORM
          </span>
        </div>

        <h1
          ref={logoRef}
          className="text-6xl md:text-8xl font-black tracking-[0.25em] text-white font-display uppercase drop-shadow-[0_0_32px_rgba(255,255,255,0.4)] opacity-0"
        >
          REVIVE
        </h1>

        <p
          ref={taglineRef}
          className="text-xs md:text-sm font-mono tracking-[0.3em] text-cyan-400 uppercase font-semibold mt-3 opacity-0"
        >
          Give Your EV a Voice.
        </p>
      </div>
    </div>
  );
};
